import { useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type IndiceCac } from "@/lib/api";
import { aPayloadIndice, etiquetaPeriodo, formatIndice, formIndice, validarIndice, type ErroresIndice, type FormIndice } from "@/lib/cac";

type Dialogo = { tipo: "form"; indice: IndiceCac | null } | { tipo: "borrar"; indice: IndiceCac } | null;
const SIN_ERRORES: ErroresIndice = {};

/** Lógica del Índice CAC (sin JSX). Todos leen; sólo el admin carga, edita y borra. PUT por período: crear y editar son lo mismo. */
export function useCacTab() {
  const { fid, membresia } = useContextoApp();
  const base = `/api/fideicomisos/${fid}/cac`;
  const lista = useApiResource<IndiceCac[]>(base);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [form, setForm] = useState<FormIndice>(() => formIndice());
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const accionGuardar = useAsyncAction((f: FormIndice) => api.put<IndiceCac>(`${base}/${f.periodo}`, aPayloadIndice(f)));
  const accionBorrar = useAsyncAction((periodo: string) => api.delete<void>(`${base}/${periodo}`).then(() => true));
  const errores = validarIndice(form);
  const cerrar = () => setDialogo(null);
  /** "Cargar mes" es un PUT: si el mes ya está, se pisa. Se avisa antes con el valor actual. */
  const yaCargado = dialogo?.tipo === "form" && !dialogo.indice ? lista.data?.find((i) => i.periodo === form.periodo) : undefined;

  const abrirForm = (indice: IndiceCac | null) => {
    setForm(formIndice(indice ?? undefined));
    setMostrarErrores(false);
    accionGuardar.limpiarError();
    setDialogo({ tipo: "form", indice });
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (Object.keys(errores).length > 0) return;
    if (await accionGuardar.run(form)) {
      cerrar();
      lista.recargar();
    }
  };

  const confirmarBorrar = async () => {
    if (dialogo?.tipo !== "borrar") return;
    if (await accionBorrar.run(dialogo.indice.periodo)) {
      cerrar();
      lista.recargar();
    }
  };

  return {
    esAdmin: membresia.rol === "admin",
    indices: lista.data ?? [],
    cargando: lista.loading && !lista.data,
    errorCarga: lista.data ? null : (lista.error?.message ?? null),
    reintentar: lista.recargar,
    dialogo,
    cerrar,
    abrirNuevo: () => abrirForm(null),
    abrirEditar: (i: IndiceCac) => abrirForm(i),
    abrirBorrar: (i: IndiceCac) => {
      accionBorrar.limpiarError();
      setDialogo({ tipo: "borrar", indice: i });
    },
    form,
    cambiar: <K extends keyof FormIndice>(campo: K, valor: FormIndice[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    errores: mostrarErrores ? errores : SIN_ERRORES,
    guardar,
    avisoReemplazo: yaCargado ? `Ya hay un valor cargado para ${etiquetaPeriodo(yaCargado.periodo)} (${formatIndice(yaCargado.valor)}): al guardar se reemplaza.` : null,
    textoGuardar: yaCargado ? "Reemplazar" : "Guardar",
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
    confirmarBorrar,
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
  };
}
